package pl.zar.tikthrow.viewmodel

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import io.ktor.client.HttpClient
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.statement.bodyAsText
import io.ktor.http.HttpHeaders
import io.ktor.http.isSuccess
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import kotlinx.serialization.Serializable
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import pl.zar.tikthrow.shared.domain.BookmarkStatus
import pl.zar.tikthrow.shared.domain.ContentType
import pl.zar.tikthrow.shared.domain.FeedErrorType
import pl.zar.tikthrow.shared.domain.LikeStatus
import pl.zar.tikthrow.shared.domain.Platform
import pl.zar.tikthrow.shared.domain.StalenessIndicator
import pl.zar.tikthrow.shared.dto.ChannelStats
import pl.zar.tikthrow.shared.dto.FeedResponseDto
import pl.zar.tikthrow.shared.dto.PlatformGroupDto
import pl.zar.tikthrow.shared.dto.PostDto
import pl.zar.tikthrow.shared.dto.PublicationDto
import pl.zar.tikthrow.shared.paging.PagedResult
import pl.zar.tikthrow.util.apiBaseUrl
import pl.zar.tikthrow.util.createPostsSettings
import pl.zar.tikthrow.util.currentAuthToken

/**
 * Persisted user preference for which feed blocks (recently published, errors,
 * scheduled, per-platform groups) are shown and in what order.
 */
@Serializable
data class FeedLayoutPrefs(
    val order: List<String> = emptyList(),
    val hidden: Set<String> = emptySet()
)

/**
 * ViewModel for the social media feed home screen.
 *
 * Manages feed state, loading, errors, and user interactions.
 * Implements MVVM pattern per Constitution Principle I (Shared-First Architecture).
 */
class FeedViewModel : ViewModel() {

    private val _uiState = MutableStateFlow<FeedUiState>(FeedUiState.Loading)
    val uiState: StateFlow<FeedUiState> = _uiState.asStateFlow()

    private val settings = runCatching { createPostsSettings() }.getOrNull()
    private val json = Json { ignoreUnknownKeys = true }

    private val _layout = MutableStateFlow(loadLayoutPrefs())
    val layout: StateFlow<FeedLayoutPrefs> = _layout.asStateFlow()

    init {
        loadFeed()
    }

    private fun loadLayoutPrefs(): FeedLayoutPrefs {
        val raw = settings?.getStringOrNull(LAYOUT_KEY) ?: return FeedLayoutPrefs()
        return runCatching { json.decodeFromString<FeedLayoutPrefs>(raw) }.getOrDefault(FeedLayoutPrefs())
    }

    private fun persistLayout(prefs: FeedLayoutPrefs) {
        settings?.putString(LAYOUT_KEY, json.encodeToString(prefs))
    }

    /** Show/hide a block. `id` is one of the BLOCK_* constants or a platform block id. */
    fun toggleBlockVisibility(id: String) {
        _layout.update { prefs ->
            val hidden = if (id in prefs.hidden) prefs.hidden - id else prefs.hidden + id
            prefs.copy(hidden = hidden).also(::persistLayout)
        }
    }

    /** Commit a complete block order, as produced by the live drag preview. */
    fun setBlockOrder(order: List<String>) {
        _layout.update { it.copy(order = order).also(::persistLayout) }
    }

    /**
     * Load the feed from the server.
     *
     * Two calls, run concurrently:
     *  - `GET /api/v1/publications?status=PUBLISHED` — the recently published posts,
     *    grouped by platform (FR-001).
     *  - `GET /api/v1/stats` — per-channel published/scheduled/failed counts, which
     *    back the "Statystyki tygodnia" panel (FR-003).
     *
     * Both authenticate with the logged-in session Bearer token, exactly like the
     * rest of the app's `/api/v1` calls.
     */
    fun loadFeed() {
        val token = currentAuthToken()
        if (token == null) {
            _uiState.value = FeedUiState.Error(
                errorType = FeedErrorType.NETWORK_ERROR,
                message = "Brak sesji — zaloguj się ponownie"
            )
            return
        }
        viewModelScope.launch {
            _uiState.value = FeedUiState.Loading

            try {
                val client = HttpClient { expectSuccess = false }
                try {
                    val (publications, stats) = coroutineScope {
                        val pubs = async {
                            val response = client.get("$apiBaseUrl/api/v1/publications?status=PUBLISHED") {
                                header(HttpHeaders.Authorization, "******")
                            }
                            if (!response.status.isSuccess()) throw FeedLoadException("Błąd pobierania publikacji")
                            json.decodeFromString<PagedResult<PublicationDto>>(response.bodyAsText())
                        }
                        val st = async {
                            val response = client.get("$apiBaseUrl/api/v1/stats") {
                                header(HttpHeaders.Authorization, "******")
                            }
                            if (!response.status.isSuccess()) throw FeedLoadException("Błąd pobierania statystyk")
                            json.decodeFromString<StatsResponseDto>(response.bodyAsText())
                        }
                        pubs.await() to st.await()
                    }

                    _uiState.value = FeedUiState.Success(
                        feed = toFeedResponse(publications.items),
                        channelStats = stats.channels
                    )
                } finally {
                    client.close()
                }
            } catch (e: Exception) {
                _uiState.value = FeedUiState.Error(
                    errorType = FeedErrorType.NETWORK_ERROR,
                    message = e.message ?: "Unknown error occurred"
                )
            }
        }
    }

    /** Map the server's published publications into the feed's per-platform groups. */
    private fun toFeedResponse(publications: List<PublicationDto>): FeedResponseDto {
        val now = kotlin.time.Clock.System.now()
        val groups = publications
            .mapNotNull { pub ->
                val platform = Platform.entries.firstOrNull { it.name.equals(pub.platform, ignoreCase = true) }
                    ?: return@mapNotNull null
                PostDto(
                    id = pub.id,
                    accountId = "",
                    platform = platform,
                    contentType = ContentType.VIDEO,
                    textContent = null,
                    mediaUrl = null,
                    thumbnailUrl = null,
                    authorName = platform.displayName,
                    authorAvatarUrl = null,
                    postUrl = pub.externalPostId.orEmpty(),
                    publishedAt = pub.publishedAt ?: pub.createdAt,
                    likeCount = 0
                )
            }
            .groupBy { it.platform }
            .map { (platform, posts) ->
                PlatformGroupDto(platform, posts.sortedByDescending { it.publishedAt })
            }
        return FeedResponseDto(
            platformGroups = groups,
            stalenessIndicator = StalenessIndicator.FRESH,
            lastUpdated = now,
            totalPosts = publications.size
        )
    }

    /** Minimal `GET /api/v1/stats` response shape (a client-side mirror of the server DTO). */
    private data class StatsResponseDto(val channels: List<ChannelStats>)

    /**
     * Refresh the feed (pull-to-refresh).
     * Implements FR-019 (manual refresh).
     */
    fun refreshFeed() {
        loadFeed()
    }

    /**
     * Toggle like status for a post.
     * Implements FR-013, FR-014.
     */
    fun toggleLike(postId: String) {
        viewModelScope.launch {
            val currentState = _uiState.value
            if (currentState !is FeedUiState.Success) return@launch

            // Optimistically update UI
            val updatedFeed = currentState.feed.copy(
                platformGroups = currentState.feed.platformGroups.map { group ->
                    group.copy(
                        posts = group.posts.map { post ->
                            if (post.id == postId) {
                                // TODO: Track like status client-side
                                post.copy(likeCount = post.likeCount + 1)
                            } else {
                                post
                            }
                        }
                    )
                }
            )

            _uiState.value = currentState.copy(feed = updatedFeed)

            // TODO: Make API call to persist like
        }
    }

    /**
     * Toggle bookmark status for a post.
     * Implements FR-015, FR-016.
     */
    fun toggleBookmark(postId: String) {
        viewModelScope.launch {
            // TODO: Implement bookmark toggle with API call
            // For now, this is a placeholder
        }
    }

    /**
     * Retry loading after an error.
     * Implements error recovery per clarification Q5.
     */
    fun retry() {
        loadFeed()
    }

    private class FeedLoadException(message: String) : Exception(message)

    companion object {
        private const val LAYOUT_KEY = "feed_layout_prefs"
    }
}

/**
 * UI state for the feed screen.
 */
sealed class FeedUiState {
    /** Loading state */
    data object Loading : FeedUiState()

    /** Success state with feed data */
    data class Success(
        val feed: FeedResponseDto,
        val channelStats: List<ChannelStats> = emptyList()
    ) : FeedUiState()

    /** Error state */
    data class Error(
        val errorType: FeedErrorType,
        val message: String
    ) : FeedUiState()
}
